/**
 * Vault Hire — AI Preferences
 *
 * Self-serve EU AI Act Art. 22 / GDPR Art. 22 opt-out at the request boundary.
 * Toggle AI globally or per workflow. Honoured BEFORE any prompt is built.
 *
 * No degraded-service messaging — "standard processing" is the framing. See
 * /app/design_guidelines.md §7 and /app/memory/PRD.md §"AI-Led Growth".
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { ShieldCheck, AlertTriangle, ToggleLeft, ToggleRight, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Toggle({ on, onChange, testId, label, description, disabled = false }) {
  return (
    <div className={`flex items-start justify-between gap-4 py-4 ${disabled ? 'opacity-60' : ''}`}>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-[var(--vh-text-primary)]">{label}</p>
        {description && <p className="text-xs text-[var(--vh-text-secondary)] mt-1 leading-relaxed">{description}</p>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        disabled={disabled}
        data-testid={testId}
        onClick={() => onChange(!on)}
        className={`shrink-0 w-12 h-7 rounded-full transition-colors flex items-center px-0.5 ${on ? 'bg-[var(--vh-brand-primary)] justify-end' : 'bg-[var(--vh-bg-muted)] border border-[var(--vh-border)] justify-start'}`}
      >
        <span className={`w-6 h-6 rounded-full bg-white shadow-sm transition ${on ? '' : 'border border-[var(--vh-border)]'}`} />
      </button>
    </div>
  );
}

export default function AIPreferencesPage() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/me/ai-preferences`);
      setData(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load AI preferences.');
    }
  };

  useEffect(() => { load(); }, []);

  const patch = async (payload) => {
    setSaving(true); setError('');
    try {
      const { data } = await axios.patch(`${API}/me/ai-preferences`, payload);
      setData(data);
      setSavedAt(new Date());
    } catch (e) {
      setError(e.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  if (!data) {
    return <div className="p-8 text-[var(--vh-text-secondary)]" data-testid="ai-prefs-loading">Loading AI preferences…</div>;
  }

  const globalOn = !!data.ai_enabled;
  const workflowValue = (id) => (id in data.workflows ? !!data.workflows[id] : true);

  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="ai-preferences-page">
      <header className="mb-8">
        <p className="text-xs uppercase tracking-[0.22em] text-[var(--vh-accent)] mb-2">Your data, your call</p>
        <h1 className="font-display text-3xl md:text-4xl text-[var(--vh-text-primary)] mb-3">AI preferences</h1>
        <p className="text-[var(--vh-text-secondary)] leading-relaxed">
          Switch AI off — globally or per feature — and Vault Hire will route every workflow you touch to a human-only path.
          Honoured at the request boundary, by default. We never train models on your data and we never share AI-derived
          inferences about you while AI is off.
        </p>
        <div className="mt-4 inline-flex items-center gap-2 text-xs text-[var(--vh-text-muted)]">
          <ShieldCheck className="w-3.5 h-3.5 text-[var(--vh-brand-primary)]" />
          EU AI Act Art. 22 · UK GDPR Art. 22 · enforced at the request boundary
        </div>
      </header>

      <section className="bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] rounded-2xl px-6 py-4">
        <Toggle
          on={globalOn}
          onChange={(next) => patch({ ai_enabled: next })}
          label="All AI features"
          description="When off, no AI feature will run anywhere in Vault Hire that processes your data. Every per-feature toggle below is overridden."
          testId="ai-prefs-global-toggle"
        />
      </section>

      <section className="mt-8 bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] rounded-2xl px-6 py-2 divide-y divide-[var(--vh-border)]">
        <header className="py-4">
          <p className="text-xs uppercase tracking-[0.18em] text-[var(--vh-accent)] mb-1">Per-feature controls</p>
          <p className="text-xs text-[var(--vh-text-secondary)]">Fine-tune which AI workflows are allowed for you. Disabling one feature here does not affect the others.</p>
        </header>
        {(data.available_workflows || []).map((wf) => (
          <Toggle
            key={wf.id}
            on={globalOn && workflowValue(wf.id)}
            disabled={!globalOn}
            onChange={(next) => patch({ workflows: { ...data.workflows, [wf.id]: next } })}
            label={wf.label}
            description={`Audience: ${wf.audience} · Data subject: ${wf.subject_kind}`}
            testId={`ai-prefs-wf-toggle-${wf.id}`}
          />
        ))}
      </section>

      <footer className="mt-8 flex items-center justify-between text-xs text-[var(--vh-text-muted)]">
        <div className="flex items-center gap-2">
          {saving && <span data-testid="ai-prefs-saving">Saving…</span>}
          {!saving && savedAt && (
            <span className="inline-flex items-center gap-1 text-[var(--vh-success)]" data-testid="ai-prefs-saved">
              <CheckCircle2 className="w-3.5 h-3.5" /> Saved {savedAt.toLocaleTimeString()}
            </span>
          )}
          {error && (
            <span className="inline-flex items-center gap-1 text-[var(--vh-accent)]" data-testid="ai-prefs-error">
              <AlertTriangle className="w-3.5 h-3.5" /> {error}
            </span>
          )}
        </div>
        <span>Changes take effect immediately. Audit-logged.</span>
      </footer>
    </div>
  );
}
